import { linkRequestsApi } from "@api";
import {
  LinkRequestStatus,
  ListIncomingPayerPayeeLinkRequests200ResponseDataInner as IncomingLinkRequest
} from "@wingspanhq/wingspan-api-sdk";

/**
 * Accepts every invitation waiting for the signed-in person, at app init.
 *
 * Takes the accepting account rather than resolving it: the accepting account
 * used to come from `listPersonStakeholders`, which is **501 Not Implemented**
 * on v3 ("scaffolded but not implemented yet"), so that lookup always threw and
 * the whole function silently did nothing behind its own `catch`. The bound
 * session account is the account the person is acting as, and `acceptLinkRequest`
 * requires it in the body (422 `ValidationError` on `acceptingAccountId`
 * otherwise).
 *
 * Failures stay swallowed on purpose — a pending invitation must never block
 * sign-in; the dashboard alert offers the same accept with visible errors.
 */
export const acceptPendingLinkRequests = async (
  acceptingAccountId: string
): Promise<IncomingLinkRequest[]> => {
  try {
    if (!acceptingAccountId) {
      return [];
    }

    // Linked ones are not accepted here; they are returned for the caller's
    // payee-context check (see `getOnboardingRedirectPath`).
    const linkRequests =
      (
        await linkRequestsApi.listIncomingPayerPayeeLinkRequests({
          filter: {
            status: {
              anyOf: [LinkRequestStatus.PENDING, LinkRequestStatus.LINKED]
            }
          }
        })
      ).data ?? [];

    const pendingLinkRequests = linkRequests.filter(
      linkRequest => linkRequest.status === LinkRequestStatus.PENDING
    );

    await Promise.all(
      pendingLinkRequests.map(linkRequest =>
        linkRequestsApi.acceptLinkRequest({
          linkRequestId: linkRequest.id,
          xWingspanAccount: acceptingAccountId,
          linkRequestAccept: { acceptingAccountId }
        })
      )
    );

    return linkRequests;
  } catch (error) {
    console.error("Failed to accept pending link requests:", error);
    return [];
  }
};
